/**
 * @file src/components/ExplanationSection.tsx
 * Explanation section with video and WhatsApp contact
 */

import { Box, Typography, Link, Paper } from '@mui/material'
import { WhatsApp } from '@mui/icons-material'
import { useMemo } from 'react'

const WHATSAPP_NUMBER = '+12672145752'
const whatsappLink = `https://wa.me/${WHATSAPP_NUMBER}`

export const ExplanationSection = () => {
  const qrCodeUrl = useMemo(() => 
    `https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(whatsappLink)}`, 
    [whatsappLink]
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: '600px', mx: 'auto' }}>
      {/* Video and Features */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          bgcolor: 'white'
        }}
      >
        <Typography variant="h6" sx={{ mb: 3, color: '#1E2A3B', fontWeight: 600, textAlign: 'center' }}>
          How it works
        </Typography>
        <Box sx={{ 
          position: 'relative',
          width: '100%',
          maxWidth: '300px',
          mx: 'auto',
          mb: 4
        }}>
          <Box sx={{ 
            position: 'relative',
            paddingTop: '177.77%', // 16:9 Aspect Ratio for vertical video
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: '#F8FAFC'
          }}>
            <iframe
              src="https://www.youtube.com/embed/0WdAXETt4yM"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 0
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </Box>
        </Box>

        {/* How to Steps */}
        <Typography variant="h6" sx={{ color: '#1E2A3B', mb: 2, fontWeight: 600, textAlign: 'center' }}>
          How to use
        </Typography>
        <Box component="ol" sx={{ 
          color: '#1E2A3B',
          pl: 2,
          maxWidth: '400px',
          mx: 'auto',
          mb: 4,
          '& > li': {
            mb: 2,
            pl: 1,
            '&:last-child': { mb: 0 }
          }
        }}>
          <li>Forward your pictures to Fowix on WhatsApp</li>
          <li>Send "ok" when you're done sending pictures</li>
          <li>Click the link we send you to access your pictures</li>
        </Box>

        {/* Features */}
        <Typography variant="h6" sx={{ color: '#1E2A3B', mb: 2, fontWeight: 600, textAlign: 'center' }}>
          Features
        </Typography>
        <Box component="ul" sx={{ 
          color: '#1E2A3B',
          pl: 2,
          maxWidth: '400px',
          mx: 'auto',
          '& > li': {
            mb: 1,
            '&:last-child': { mb: 0 }
          }
        }}>
          <li>Select specific images to include in your PDF</li>
          <li>Drag and drop to reorder images exactly how you want</li>
          <li>Download your PDF instantly to your device</li>
          <li>Send the PDF directly to your email</li>
        </Box>
      </Paper>

      {/* WhatsApp Contact */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'grey.200',
          bgcolor: 'white',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Typography variant="h6" sx={{ mb: 3, color: '#1E2A3B', fontWeight: 600, textAlign: 'center' }}>
          Start converting your images
        </Typography>
        <Box sx={{ mb: 3 }}>
          <img 
            src={qrCodeUrl} 
            alt="WhatsApp QR Code" 
            style={{ 
              width: 150, 
              height: 150,
              borderRadius: 8,
              backgroundColor: 'white',
              padding: 8,
              border: '1px solid #E2E8F0'
            }} 
          />
        </Box>
        <Link
          href={whatsappLink}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: '#25D366',
            textDecoration: 'none',
            py: 1,
            px: 2,
            borderRadius: 1,
            transition: 'background-color 0.2s',
            '&:hover': { 
              backgroundColor: 'rgba(37, 211, 102, 0.08)'
            }
          }}
        >
          <WhatsApp />
          <Typography sx={{ fontWeight: 500 }}>
            Open in WhatsApp
          </Typography>
        </Link>
      </Paper>
    </Box>
  )
} 